import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import sv from 'date-fns/locale/sv'
import React, {useState} from 'react'
import {useForm} from '../../../common/hooks/useForm'
import {useNotification} from '../../../common/notifications/useNotification'
import {BoxWithoutRentals} from '../../../common/types/box'
import {formatDate, startOfYear, subDays, subMonths, subYears} from '../../../common/utils/dateUtils'
import {resolveBoxDisplayName} from '../../../common/utils/resolveBoxDisplayName'
import {Accordion} from '../../../webui/Accordion'
import {AccordionDetails} from '../../../webui/AccordionDetails'
import {AccordionSummary} from '../../../webui/AccordionSummary'
import {Box} from '../../../webui/Box'
import {Button} from '../../../webui/Button'
import {MenuItem} from '../../../webui/inputs/MenuItem'
import {SearchSelect, SearchSelectProps} from '../../../webui/inputs/SearchSelect'
import {Select, SelectChangeEvent} from '../../../webui/inputs/Select'
import {INPUT_WIDTH} from '../../../webui/inputs/utils'
import {Typography} from '../../../webui/Typography'
import {sortBoxes} from '../../box/utils/sortBoxes'
import {type ProductReplacerRentalFilter as ProductReplacerRentalFilterType} from './types'

interface Props {
  allBoxes: BoxWithoutRentals[]
  initialFilter: ProductReplacerRentalFilterType
  onFilterChange: (filter: ProductReplacerRentalFilterType) => void
}

export const ProductReplacerRentalFilter: React.FC<Props> = ({allBoxes, initialFilter, onFilterChange}) => {
  const {showNotification} = useNotification()
  const [filterHasChanged, setFilterHasChanged] = useState<boolean>(false)
  const {form, updateField} = useForm<ProductReplacerRentalFilterType>(initialFilter)
  const [selectedRange, setSelectedRange] = useState<string>('')

  const onApplyFilter = () => {
    onFilterChange(form)
    setFilterHasChanged(false)
    showNotification({
      message: 'Filter applied',
      severity: 'success',
    })
  }

  const onBoxChange: SearchSelectProps['onChange'] = (value) => {
    updateField('selectedBoxes', value ? [value] : [])
    setFilterHasChanged(true)
  }

  const onFromDateChange = (value: Date | null) => {
    updateField('fromDate', value ? formatDate(value) : '')
    setFilterHasChanged(true)
  }

  const onEndDateChange = (value: Date | null) => {
    updateField('endDate', value ? formatDate(value) : '')
    setFilterHasChanged(true)
  }

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value
    setSelectedRange(selectedValue)

    let fromDate = new Date()
    switch (selectedValue) {
      case 'lastWeek':
        fromDate = subDays(new Date(), 7)
        break
      case 'lastMonth':
        fromDate = subDays(new Date(), 30)
        break
      case 'lastThreeMonths':
        fromDate = subMonths(new Date(), 3)
        break
      case 'thisYear':
        fromDate = startOfYear(new Date())
        break
      case 'oneYearBack':
        fromDate = subYears(new Date(), 1)
        break
      default:
        break
    }

    updateField('fromDate', formatDate(fromDate))
    updateField('endDate', formatDate(new Date()))
    setFilterHasChanged(true)
  }

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="filter-content" id="filter-header">
        <Typography variant="h6">Filter</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
          <Box py={2} display="flex" flexDirection={{xs: 'column', sm: 'row'}} alignItems={{xs: 'stretch', sm: 'center'}} gap={2}>
            <DatePicker
              label="Från"
              value={form.fromDate ? new Date(form.fromDate) : null}
              onChange={onFromDateChange}
              sx={{minWidth: INPUT_WIDTH}}
            />
            <Box ml={2} />
            <DatePicker label="Till" value={form.endDate ? new Date(form.endDate) : null} onChange={onEndDateChange} sx={{minWidth: INPUT_WIDTH}} />
            <Box ml={2} />
            <Select id="date-range-select" label={'Snabbval datum'} value={selectedRange} variant={'outlined'} onChange={handleDateRangeChange}>
              <MenuItem value="lastWeek">Senaste veckan</MenuItem>
              <MenuItem value="lastMonth">Senaste månaden</MenuItem>
              <MenuItem value="lastThreeMonths">Senaste 3 månaderna</MenuItem>
              <MenuItem value="thisYear">Detta året</MenuItem>
              <MenuItem value="oneYearBack">Ett år tillbaka</MenuItem>
            </Select>
          </Box>
          <Box py={2} display="flex" flexDirection={{xs: 'column', sm: 'row'}} alignItems={{xs: 'stretch', sm: 'flex-end'}} gap={2}>
            <SearchSelect
              label={'Box'}
              onChange={onBoxChange}
              options={sortBoxes(allBoxes).map((box) => ({
                label: resolveBoxDisplayName(box),
                value: box.id,
              }))}
              value={form.selectedBoxes[0] ?? null}
            />
            <Box ml={2} />
            <Button disabled={!filterHasChanged} onClick={onApplyFilter}>
              Använd filter
            </Button>
          </Box>
        </LocalizationProvider>
      </AccordionDetails>
    </Accordion>
  )
}
